import { Injectable, signal, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  AuthErrorLocale,
  DEFAULT_AUTH_ERROR_LOCALE,
  getAvailableAuthErrorLocales,
  getLocalizedAuthError,
  LocalizedAuthError
} from '../i18n/auth-errors.translations';
import { AuthErrorCode } from '../models/auth-errors.model';
import { LoggerService } from './logger.service';
import { AppStorageService } from './app-storage.service';

/**
 * LocaleService
 * DEV-267: Low Priority - i18n support for error messages
 *
 * Provides locale management and translation services for the application.
 * Uses Angular signals for reactive locale changes.
 *
 * Usage:
 * ```typescript
 * // Inject the service
 * private localeService = inject(LocaleService);
 *
 * // Get current locale
 * const currentLocale = this.localeService.currentLocale();
 *
 * // Change locale
 * this.localeService.setLocale('es');
 *
 * // Get translated error message
 * const error = this.localeService.getAuthErrorMessage(AuthErrorCode.INVALID_CREDENTIALS);
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class LocaleService {
  /**
   * Logger service for consistent logging
   */
  private readonly logger = inject(LoggerService);

  /**
   * Platform ID for SSR compatibility
   */
  private readonly platformId = inject(PLATFORM_ID);

  /**
   * Browser platform check for SSR compatibility
   */
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  /**
   * SSR-safe storage wrapper (GAP-FE-C013). Declared before the
   * `_currentLocale` signal so the `loadSavedLocale()` initializer can use it.
   */
  private readonly appStorage = inject(AppStorageService);

  /**
   * Storage key for persisting locale preference
   */
  private readonly LOCALE_STORAGE_KEY = 'razor_auth_locale';

  /**
   * Available locales - initialized first as it's used by loadSavedLocale
   */
  readonly availableLocales = getAvailableAuthErrorLocales();

  /**
   * Current locale signal - initialized after availableLocales
   */
  private readonly _currentLocale = signal<AuthErrorLocale>(this.loadSavedLocale());

  /**
   * Readonly accessor for current locale
   */
  readonly currentLocale = this._currentLocale.asReadonly();

  /**
   * Locale display names for UI
   */
  readonly localeDisplayNames: Record<AuthErrorLocale, string> = {
    en: 'English',
    es: 'Espa\u00f1ol',
    de: 'Deutsch',
    fr: 'Fran\u00e7ais',
    zh: '\u4e2d\u6587',
    ja: '\u65e5\u672c\u8a9e'
  };

  /**
   * Load saved locale from storage or detect browser locale
   */
  private loadSavedLocale(): AuthErrorLocale {
    // Try to load from storage (browser only)
    if (this.isBrowser) {
      // GAP-FE-C013: SSR-safe wrapper; preserves the LOCALE_STORAGE_KEY key.
      const saved = this.appStorage.getItem(this.LOCALE_STORAGE_KEY);
      if (saved && this.isValidLocale(saved)) {
        return saved as AuthErrorLocale;
      }

      // Try to detect from browser language
      try {
        const browserLang = navigator.language?.split('-')[0];
        if (browserLang && this.isValidLocale(browserLang)) {
          return browserLang as AuthErrorLocale;
        }
      } catch (error) {
        this.logger.warn('[LocaleService] Failed to detect browser language:', { error });
      }
    }

    // Fall back to default
    return DEFAULT_AUTH_ERROR_LOCALE;
  }

  /**
   * Check if a locale string is valid
   */
  private isValidLocale(locale: string): boolean {
    // Defensive check for initialization order in testing environments
    if (!this.availableLocales || !Array.isArray(this.availableLocales)) {
      return false;
    }
    return this.availableLocales.includes(locale as AuthErrorLocale);
  }

  /**
   * Set the current locale
   * @param locale The locale code to set
   * @returns true if locale was set, false if invalid
   */
  setLocale(locale: AuthErrorLocale): boolean {
    // Sanitize input - only allow alphanumeric characters
    const sanitized = String(locale).replace(/[^a-z0-9]/gi, '').toLowerCase();

    if (!this.isValidLocale(sanitized)) {
      this.logger.warn('[LocaleService] Invalid locale', { locale, available: this.availableLocales });
      return false;
    }

    this._currentLocale.set(sanitized as AuthErrorLocale);

    // Persist to storage (browser only)
    // GAP-FE-C013: SSR-safe, quota-resilient wrapper; preserves the LOCALE_STORAGE_KEY key.
    this.appStorage.setItem(this.LOCALE_STORAGE_KEY, sanitized);

    return true;
  }

  /**
   * Get localized auth error message for a given error code
   * Uses current locale with fallback to English
   *
   * @param code The auth error code
   * @param locale Optional locale override (uses current locale if not provided)
   * @returns Localized error with message and optional recovery guidance
   */
  getAuthErrorMessage(
    code: AuthErrorCode,
    locale?: AuthErrorLocale
  ): LocalizedAuthError {
    const targetLocale = locale || this._currentLocale();
    return getLocalizedAuthError(code, targetLocale);
  }

  /**
   * Get the display name for the current locale
   */
  getCurrentLocaleDisplayName(): string {
    return this.localeDisplayNames[this._currentLocale()];
  }

  /**
   * Get display name for a specific locale
   */
  getLocaleDisplayName(locale: AuthErrorLocale): string {
    return this.localeDisplayNames[locale] || locale;
  }

  /**
   * Reset locale to default
   */
  resetToDefault(): void {
    this.setLocale(DEFAULT_AUTH_ERROR_LOCALE);
  }

  /**
   * Clear saved locale preference
   */
  clearSavedLocale(): void {
    // GAP-FE-C013: SSR-safe wrapper; preserves the LOCALE_STORAGE_KEY key.
    this.appStorage.removeItem(this.LOCALE_STORAGE_KEY);
    this._currentLocale.set(DEFAULT_AUTH_ERROR_LOCALE);
  }
}
